import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Table, Card, Spin, Tag, Button, Space } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import moment from "moment";

import { fetchCustomerList } from "../../../utils/CustomerService/api.js";
import ErrorBlock from "../../../components/UI/ErrorBlock.js";
import "../../../App.css";
import SearchBar from "../../../components/SearchBar/SearchBar.js";

const { Column, ColumnGroup } = Table;

function CustomerServiceList() {
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);

  const navigate = useNavigate();

  const {
    data: initialData,
    isLoading: isInitialLoading,
    isError,
    refetch
  } = useQuery({
    queryKey: ["customer",searchText, currentPage, pageSize],
    queryFn: () =>
      fetchCustomerList({
        searchText,
        page: currentPage,
        pageSize,
      }),
    keepPreviousData: true,
  });

  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const dataSource = initialData?.results?.map((customer) => ({
    key: customer.id,
    customerName:
      customer?.userAccount?.first_name +
      " " +
      customer?.userAccount?.last_name,
    phoneNumber: customer.phoneNumber,
    trialPoint: customer.trial_point,
    depositedPoint: customer.deposited_point,
    balance: customer.balance,
    totalReservations: customer.total_reservation,
    totalTransacation: customer.total_transacation,
    lastActivity: moment(customer.created_at).format("MMM D, YYYY h:mm A"),
    status: customer.is_approved,
    profile: customer.profilePicture,
    isSMSsent: customer.is_sms_sent,
    createdAt: moment(customer.created_at).format("MMM D, YYYY h:mm A"),
  }));

  const handleViewDetail = (record) => {
    navigate("/customer-Profile", { state: { record } });
  };

  if (isError) {
    return (
      <ErrorBlock
        title="Failed to fetch data"
        message="Customer Service data not found."
      />
    );
  }

  const handleSearch = (value) => {
    setSearchText(value);
    refetch();
  };

  return (
    <>
      <div className="p-4 bg-white rounded-lg shadow-md dark:bg-gray-900 text-gray-900 dark:text-white">
        <SearchBar onSearch={handleSearch} className="py-2 w-full mb-4" />
        {isInitialLoading ? (
          <Card>
            <Spin /> Loading ...
          </Card>
        ) : (
          <Table
            dataSource={dataSource}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: initialData?.totalCount || 0,
              onChange: handlePageChange,
              showSizeChanger: true,
              responsive: true,
            }}
            scroll={{ x: true }}
            className="responsive-table"
          >
            <ColumnGroup>
              <Column
                title="Customer Name"
                key="customerName"
                dataIndex="customerName"
              />
              <Column
                title="Phone Number"
                key="phoneNumber"
                dataIndex="phoneNumber"
              />
              <Column
                title="Trial Point"
                key="trialPoint"
                dataIndex="trialPoint"
              />
              <Column
                title="Deposited Point"
                key="depositedPoint"
                dataIndex="depositedPoint"
              />
              <Column
                title="Total Reservations"
                key="totalReservations"
                dataIndex="totalReservations"
              />
              <Column
                title="Last Activity"
                key="lastActivity"
                dataIndex="lastActivity"
              />
              <Column
                title="Status"
                key="status"
                dataIndex="status"
                render={(status) => (
                  <Tag
                    className={
                      status === true
                        ? "bg-green-600 text-white px-4 py-1 rounded-md"
                        : "bg-orange-400 text-white px-4 py-1 rounded-md"
                    }
                  >
                    {status ? "Completed" : "Pending"}
                  </Tag>
                )}
              />
              <Column
                title="Profile"
                key="profile"
                dataIndex="profile"
                render={(_, record) => (
                  <Space size="middle" style={{ color: "blue" }}>
                    <Button
                      type="default"
                      className="bg-blue-100 text-blue-500 px-4 w-30 rounded-md"
                      onClick={() => handleViewDetail(record)}
                    >
                      <EyeOutlined />
                      View
                    </Button>
                  </Space>
                )}
              />
            </ColumnGroup>
          </Table>
        )}
      </div>
    </>
  );
}

export default CustomerServiceList;
